import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { ROUTES } from '@/core/constants/routes';
import { Toolbar } from '@/shared/components/custom/toolbar/Toolbar';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/components/ui/tabs';
import { useCanManageOrg } from '@/features/organizations/public';
import {
  UNDERLINE_TABS_LIST_CLASS,
  UNDERLINE_TABS_TRIGGER_CLASS,
} from '../components/underlineTabStyles';
import { TimeOffActivationGate } from '../components/activation/TimeOffActivationGate';
import { MyCalendarView } from '../components/calendar/MyCalendarView';
import { RequestForm } from '../components/requests/RequestForm';
import { RequestDetail } from '../components/requests/RequestDetail';
import { PendingApprovalsList } from '../components/approvals/PendingApprovalsList';
import { usePendingTimeOffApprovals } from '../hooks/usePendingTimeOffApprovals';
import { TeamSection } from '../components/team/TeamSection';
import { TimeOffConfigTab } from '../components/settings/TimeOffConfigTab';

type TimeOffTab = 'calendar' | 'approvals' | 'team' | 'settings';

export default function TimeOffPage() {
  const { t } = useTranslation('timeOff');
  const navigate = useNavigate();
  // Deep-link entry point from a notification: `/:orgId/time-off/requests/:requestId`.
  const { orgId, requestId: deepLinkedRequestId } = useParams<{
    orgId: string;
    requestId?: string;
  }>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    deepLinkedRequestId ?? null,
  );
  const [activeTab, setActiveTab] = useState<TimeOffTab>('calendar');
  const canManageOrg = useCanManageOrg();
  const { data: pendingApprovals } = usePendingTimeOffApprovals();
  const pendingCount = pendingApprovals?.length ?? 0;

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="time-off-page">
      <TimeOffActivationGate>
        <Toolbar>
          {activeTab === 'approvals' ? (
            <Toolbar.Info
              title={t('approvals.pageTitle')}
              description={t('approvals.pageDescription')}
            />
          ) : (
            <Toolbar.Info title={t('nav.pageTitle')} description={t('nav.description')} />
          )}
          <Toolbar.Actions>
            {activeTab === 'approvals' && pendingCount > 0 && (
              <Badge variant="secondary" data-testid="time-off-approvals-pending-badge">
                {t('approvals.pendingBadge', { count: pendingCount })}
              </Badge>
            )}
            {activeTab === 'calendar' && (
              <Button
                onClick={() => setIsCreateOpen(true)}
                data-testid="time-off-create-request-btn"
              >
                <Plus className="size-4" />
                {t('requests.form.title')}
              </Button>
            )}
          </Toolbar.Actions>
        </Toolbar>

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as TimeOffTab)}
          className="mt-6"
        >
          <TabsList className={UNDERLINE_TABS_LIST_CLASS}>
            <TabsTrigger value="calendar" className={UNDERLINE_TABS_TRIGGER_CLASS}>
              {t('nav.calendarTab')}
            </TabsTrigger>
            <TabsTrigger value="approvals" className={UNDERLINE_TABS_TRIGGER_CLASS}>
              {t('approvals.title')}
            </TabsTrigger>
            {canManageOrg && (
              <TabsTrigger
                value="team"
                className={UNDERLINE_TABS_TRIGGER_CLASS}
                data-testid="time-off-team-tab"
              >
                {t('team.title')}
              </TabsTrigger>
            )}
            {canManageOrg && (
              <TabsTrigger
                value="settings"
                className={UNDERLINE_TABS_TRIGGER_CLASS}
                data-testid="time-off-settings-tab"
              >
                {t('nav.settingsTab')}
              </TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="calendar" className="space-y-4">
            <MyCalendarView onSelectRequest={setSelectedRequestId} />
          </TabsContent>
          <TabsContent value="approvals">
            <PendingApprovalsList />
          </TabsContent>
          {canManageOrg && (
            <TabsContent value="team">
              <TeamSection />
            </TabsContent>
          )}
          {canManageOrg && (
            <TabsContent value="settings">
              <TimeOffConfigTab />
            </TabsContent>
          )}
        </Tabs>

        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogContent data-testid="time-off-create-request-dialog">
            <DialogHeader>
              <DialogTitle>{t('requests.form.title')}</DialogTitle>
            </DialogHeader>
            {/* Stays open on success — RequestForm shows the approval chain inline (see doc's "show Steps[] before closing"). */}
            <RequestForm onCancel={() => setIsCreateOpen(false)} />
          </DialogContent>
        </Dialog>

        <Dialog
          open={!!selectedRequestId}
          onOpenChange={(open) => {
            if (open) return;
            setSelectedRequestId(null);
            if (deepLinkedRequestId && orgId) navigate(ROUTES.timeOff(orgId), { replace: true });
          }}
        >
          <DialogContent data-testid="time-off-request-detail-dialog">
            <DialogHeader>
              <DialogTitle>{t('requests.detail.title')}</DialogTitle>
            </DialogHeader>
            {selectedRequestId && <RequestDetail requestId={selectedRequestId} />}
          </DialogContent>
        </Dialog>
      </TimeOffActivationGate>
    </div>
  );
}
